import React, { useState, useEffect, useRef } from 'react';
import { Phone, Mail, MapPin, Scissors, Flower2, ChevronRight, Trash2, Instagram, Home as HomeIcon, Star, ArrowRight, CheckCircle2, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { FallingLeaves } from '../components/FallingLeaves';
import { SectionDecor, GrassDivider } from '../components/SectionDecor';
import { HeroTree } from '../components/HeroScenery';
import { HeroMower } from '../components/HeroMower';
import { useScrollReveal } from '../hooks/useScrollReveal';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

// Module-scope constants — stable references avoid stale-closure warnings in useEffect
const DEFAULT_GALLERY_IMAGES = [
  'https://customer-assets.emergentagent.com/job_walker-lawn-care/artifacts/pm1vaqfa_IMG_0856.jpeg',
  'https://customer-assets.emergentagent.com/job_walker-lawn-care/artifacts/d2rqp3b8_IMG_0854.jpeg',
  'https://customer-assets.emergentagent.com/job_walker-lawn-care/artifacts/ssthkmpf_IMG_0861.jpeg',
  'https://customer-assets.emergentagent.com/job_walker-lawn-care/artifacts/eve9ot7l_IMG_0855.jpeg',
  'https://customer-assets.emergentagent.com/job_walker-lawn-care/artifacts/5w6c6ehq_IMG_0860.jpeg',
  'https://customer-assets.emergentagent.com/job_walker-lawn-care/artifacts/fmb9tbc1_IMG_0859.jpeg'
];

const DEFAULT_REVIEWS = [
  {
    name: 'Leah C.',
    location: 'Edmond',
    rating: 5,
    text: "I've known Daniel since he was 14. He started out working in my gardens with his sister. Never will you find a harder worker than Daniel. He saved his money so he could eventually start his own lawn business. You will count yourself very fortunate if you hire him."
  },
  {
    name: 'Aloma A.',
    location: 'Midwest City',
    rating: 5,
    text: "Daniel has been doing our lawn for the past 4 - 5 years and he is just great. He's conscientious, always on time, and so dependable and thorough. You can't do better."
  },
  {
    name: 'Holly K.',
    location: 'Del City',
    rating: 5,
    text: "All I can say is WOW! Daniel is polite, respectful and has a work ethic that is UNBEATABLE! He has renewed my hope for the future of his generation! My front yard is something to be proud of again!"
  },
  {
    name: 'Kallie S.',
    location: 'Edmond',
    rating: 5,
    text: "1000% recommend!! he's a good respectable kid!"
  },
  {
    name: 'Jim T.',
    location: 'Choctaw',
    rating: 5,
    text: "He has done my lawn since last year and I am very pleased with his work. He is a fine young man!"
  },
  {
    name: 'Robert M.',
    location: 'Oklahoma City',
    rating: 5,
    text: "Best lawn service in OKC! Daniel always shows up on time and does exceptional work. My yard looks professionally maintained and the neighbors have noticed. Fair pricing and great communication."
  },
  {
    name: 'Sarah B.',
    location: 'Edmond',
    rating: 5,
    text: "We've tried several lawn care companies over the years, but Daniel is by far the best! He takes pride in his work and it really shows. Our flower beds have never looked better."
  },
  {
    name: 'Michael P.',
    location: 'Choctaw',
    rating: 5,
    text: "Daniel transformed our overgrown yard into something beautiful! His attention to detail is incredible. He edged perfectly along all the walkways and even cleaned up some areas we didn't ask him to."
  },
  {
    name: 'Jessica R.',
    location: 'Oklahoma City',
    rating: 5,
    text: "Such a refreshing experience! Daniel is courteous, hard-working, and does amazing work. Our lawn is the best it's looked in years. Thank you!"
  },
  {
    name: 'David L.',
    location: 'Midwest City',
    rating: 5,
    text: "I'm impressed with the quality and consistency! Daniel has been maintaining our lawn all season and every single time it looks perfect. Highly recommend Walker Lawn Care!"
  }
];

const Home = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    message: ''
  });

  const [loginData, setLoginData] = useState({
    username: '',
    password: ''
  });

  const [showAllReviews, setShowAllReviews] = useState(false);
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hideHeader, setHideHeader] = useState(false);
  const lastScrollY = React.useRef(0);
  const [reviews, setReviews] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);

  useEffect(() => {
    const onScroll = () => {
      const currentY = window.scrollY;
      setScrolled(currentY > 40);
      // Hide header on mobile when scrolling down, show when scrolling up
      if (currentY > lastScrollY.current && currentY > 100) {
        setHideHeader(true);
      } else {
        setHideHeader(false);
      }
      lastScrollY.current = currentY;
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    axios.get('/api/reviews').then(res => {
      if (res.data && res.data.length > 0) {
        // Merge: defaults first, then DB additions
        const dbNames = new Set(res.data.map(r => r.name?.toLowerCase()));
        const uniqueDefaults = DEFAULT_REVIEWS.filter(r => !dbNames.has(r.name?.toLowerCase()));
        setReviews([...uniqueDefaults, ...res.data]);
      } else {
        setReviews(DEFAULT_REVIEWS);
      }
    }).catch(() => setReviews(DEFAULT_REVIEWS));

    axios.get('/api/gallery').then(res => {
      if (res.data && res.data.length > 0) {
        // Merge: defaults first, then DB additions
        const dbUrls = new Set(res.data.map(p => p.url));
        const uniqueDefaults = DEFAULT_GALLERY_IMAGES.filter(url => !dbUrls.has(url));
        setGalleryImages([...uniqueDefaults, ...res.data.map(p => p.url)]);
      } else {
        setGalleryImages(DEFAULT_GALLERY_IMAGES);
      }
    }).catch(() => setGalleryImages(DEFAULT_GALLERY_IMAGES));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`/api/contact`, formData);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setFormData({ name: '', email: '', phone: '', location: '', message: '' });
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(error.response?.status === 429 ? error.response.data.detail : 'Sorry, there was an error. Please call us at (572) 205-7331.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format a phone number to (XXX) XXX-XXXX while user types
  const formatPhone = (value) => {
    const digits = (value || '').replace(/\D/g, '').slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === 'phone' ? formatPhone(value) : value;
    setFormData({ ...formData, [name]: nextValue });
  };

  const handleLoginChange = (e) => {
    setLoginData({ ...loginData, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    
    try {
      const response = await axios.post(`/api/auth/login`, loginData, {
        withCredentials: true
      });
      
      toast.success(`Welcome back, ${response.data.name}!`);
      setLoginData({ username: '', password: '' });
      navigate('/dashboard');
      
    } catch (error) {
      console.error('Login error:', error);
      if (error.response?.status === 401) {
        toast.error('Invalid username or password');
      } else if (error.response?.status === 429) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Login failed. Please try again.');
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  useScrollReveal([reviews, galleryImages, showAllReviews, showAllPhotos]);

  const visibleReviews = showAllReviews ? reviews : reviews.slice(0, 3);

  const visiblePhotos = showAllPhotos ? galleryImages : galleryImages.slice(0, 3);

  // Grass blade silhouette strip (used at the bottom of the hero)
  const grassBorderStyle = {
    backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 600 40' preserveAspectRatio='none' width='600' height='40'><path d='M0 40 L6 18 L11 40 L17 8 L23 40 L29 16 L35 4 L43 40 L50 12 L57 40 L64 20 L72 6 L80 40 L88 16 L96 40 L104 10 L112 40 L120 22 L128 40 L136 8 L144 40 L152 18 L160 4 L168 40 L176 14 L184 40 L192 22 L200 6 L208 40 L216 16 L224 40 L232 10 L240 40 L248 20 L256 40 L264 8 L272 40 L280 18 L288 4 L296 40 L304 12 L312 40 L320 22 L328 6 L336 40 L344 18 L352 40 L360 8 L368 40 L376 16 L384 40 L392 24 L400 4 L408 40 L416 14 L424 40 L432 20 L440 6 L448 40 L456 18 L464 40 L472 10 L480 40 L488 22 L496 40 L504 8 L512 40 L520 16 L528 4 L536 40 L544 12 L552 40 L560 20 L568 6 L576 40 L584 18 L592 40 L600 12 L600 40 Z' fill='%2365a30d' opacity='0.7'/></svg>")`,
    backgroundRepeat: 'repeat-x',
    backgroundPosition: 'bottom',
    backgroundSize: '600px 40px',
  };

  const services = [
    {
      icon: <Scissors className="h-8 w-8" />,
      title: 'Lawn Mowing',
      description: 'Precision cuts for a perfectly manicured lawn every time.'
    },
    {
      icon: <Scissors className="h-8 w-8" />,
      title: 'Edging',
      description: 'Clean, crisp edges along sidewalks, driveways, and beds.'
    },
    {
      icon: <Scissors className="h-8 w-8" />,
      title: 'Trimming',
      description: 'Expert trimming around obstacles, fences, and hard-to-reach areas.'
    },
    {
      icon: <Flower2 className="h-8 w-8" />,
      title: 'Flower Beds',
      description: 'Design and maintenance to enhance your curb appeal.'
    },
    {
      icon: <Trash2 className="h-8 w-8" />,
      title: 'Junk Removal',
      description: 'Quick removal of yard waste and small debris.'
    }
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Work Sans', sans-serif" }}>
      {/* Header */}
      <header className={`fixed top-0 w-full z-50 transition-all duration-300 bg-white shadow-sm border-b border-stone-200 ${hideHeader ? 'md:translate-y-0 -translate-y-full' : 'translate-y-0'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile Header */}
          <div className="md:hidden flex justify-between items-center py-3">
            <img 
              src="https://customer-assets-gfyr7b9c.emergentagent.net/job_5647de8f-979b-478d-b7c4-4f8dd1ff35c7/artifacts/13e2fefbb0813f34_C42ABAAC-2265-4F8D-9635-D61DF4287EC2.webp" 
              alt="Walker Lawn Care Logo" 
              className="h-11 w-auto max-w-[60vw] object-contain" data-testid="header-logo-mobile"
            />
            <a 
              href="tel:5722057331" 
              className="flex items-center gap-1.5 bg-green-700 text-white px-4 py-2 rounded-full text-xs font-semibold"
            >
              <Phone className="h-3.5 w-3.5" />
              Call Us
            </a>
          </div>
          
          {/* Desktop Header */}
          <div className="hidden md:flex justify-between items-center py-4">
            <div className="flex items-center">
              <img 
                src="https://customer-assets-gfyr7b9c.emergentagent.net/job_5647de8f-979b-478d-b7c4-4f8dd1ff35c7/artifacts/13e2fefbb0813f34_C42ABAAC-2265-4F8D-9635-D61DF4287EC2.webp" 
                alt="Walker Lawn Care Logo" 
                className="h-14 lg:h-16 w-auto object-contain" data-testid="header-logo"
              />
            </div>
            <nav className="flex items-center gap-1 bg-stone-50 px-2 py-1.5 rounded-full border border-stone-200" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {['About', 'Services', 'Gallery', 'Reviews', 'Contact'].map(item => (
                <a 
                  key={item}
                  href={`#${item.toLowerCase()}`} 
                  className="text-sm font-medium tracking-wide px-4 py-2 rounded-full transition-all text-stone-700 hover:bg-lime-100 hover:text-green-700"
                >
                  {item}
                </a>
              ))}
            </nav>
            <a 
              href="tel:5722057331" 
              data-testid="header-phone-link"
              className="hidden lg:flex items-center gap-2 bg-green-700 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-green-800 transition-all hover:shadow-lg"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              <Phone className="h-4 w-4" />
              (572) 205-7331
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section - Light Background */}
      <section className="relative pt-24 md:pt-28 lg:pt-24 pb-20 md:pb-28 md:min-h-[calc(100vh-3.5rem)] md:flex md:items-center bg-gradient-to-b from-lime-100 via-lime-50 to-white overflow-hidden">
        <FallingLeaves />
        <HeroTree />
        {/* Grass blade silhouette at bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-10 pointer-events-none" style={grassBorderStyle} aria-hidden="true" />
        <HeroMower />
        <div className="relative z-10 w-full max-w-[1500px] mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1fr_1.25fr] gap-10 lg:gap-12 items-center lg:items-start">
            <div className="lg:pt-6">
              <div className="inline-flex items-center gap-2 bg-green-100 border border-green-200 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <CheckCircle2 className="h-4 w-4" />
                Trusted by 100+ OKC Homeowners
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-stone-900 mb-5 leading-[1.08] tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Your Lawn,
                <span className="block text-lime-600 mt-1">Our Passion.</span>
              </h1>

              <div className="lg:hidden mb-6 rounded-2xl overflow-hidden shadow-xl ring-1 ring-stone-900/10">
                <img
                  src="https://customer-assets-gfyr7b9c.emergentagent.net/job_5647de8f-979b-478d-b7c4-4f8dd1ff35c7/artifacts/0e3e5562a5eda35f_IMG_2183.jpeg"
                  alt="Walker Lawn Care - Reliable, Affordable, Local"
                  data-testid="hero-image-mobile"
                  className="w-full h-auto block"
                />
              </div>
              
              <p className="text-base sm:text-lg text-stone-600 mb-8 leading-relaxed max-w-xl">
                Professional lawn mowing, edging, trimming, and flower bed care — serving Oklahoma City and surrounding areas since 2023.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  size="lg" 
                  data-testid="hero-get-quote-btn"
                  className="bg-green-600 hover:bg-green-700 text-white px-7 py-5 sm:py-6 text-base sm:text-lg rounded-full shadow-lg hover:shadow-xl transition-all font-semibold"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                  onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                >
                  Get Free Quote
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  size="lg" 
                  data-testid="hero-call-btn"
                  className="bg-stone-900 text-white hover:bg-stone-800 px-7 py-5 sm:py-6 text-base sm:text-lg rounded-full transition-all font-semibold"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                  onClick={() => window.location.href = 'tel:5722057331'}
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Call Us
                </Button>
              </div>
            </div>
            
            {/* Hero Image */}
            <div className="relative hidden lg:block lg:pt-6 lg:pl-10 2xl:-mr-10">
              <div className="rounded-3xl overflow-hidden shadow-2xl ring-1 ring-stone-900/10">
                <img 
                  src="https://customer-assets-gfyr7b9c.emergentagent.net/job_5647de8f-979b-478d-b7c4-4f8dd1ff35c7/artifacts/0e3e5562a5eda35f_IMG_2183.jpeg" 
                  alt="Walker Lawn Care - Reliable, Affordable, Local" 
                  data-testid="hero-image"
                  className="w-full h-auto block"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-stone-900 py-4">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-6 sm:gap-16 items-center text-white/90" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <div className="flex items-center gap-2 text-sm font-medium">
              <Star className="h-5 w-5 text-lime-400 fill-lime-400" />
              5-Star Rated
            </div>
            <div className="flex items-center gap-2 text-sm font-medium">
              <CheckCircle2 className="h-5 w-5 text-lime-400" />
              Free Estimates
            </div>
            <div className="flex items-center gap-2 text-sm font-medium">
              <CheckCircle2 className="h-5 w-5 text-lime-400" />
              Serving OKC Since 2023
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="relative overflow-hidden py-16 md:py-24 lg:py-32 bg-lime-50">
        <SectionDecor />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-medium text-green-700 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>What We Do</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Our Services
            </h2>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 lg:gap-5">
            {services.map((service, index) => (
              <Card 
                key={service.title} 
                data-testid={`service-card-${index}`}
                className="border border-stone-200 bg-white hover:shadow-xl hover:-translate-y-1 transition-all duration-300 rounded-2xl overflow-hidden"
              >
                <CardContent className="p-4 sm:p-5 lg:p-6">
                  <div className="bg-lime-100 w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center text-green-700 mb-3 sm:mb-4">
                    {React.cloneElement(service.icon, { className: 'h-5 w-5 sm:h-7 sm:w-7' })}
                  </div>
                  <h3 className="text-sm sm:text-base lg:text-lg font-bold text-stone-900 mb-1 sm:mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{service.title}</h3>
                  <p className="text-stone-600 text-xs sm:text-sm leading-relaxed hidden sm:block">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
        <GrassDivider color="#9ccc65" />
      </section>

      {/* Service Areas */}
      <section id="areas" className="relative overflow-hidden py-16 md:py-24 lg:py-32 bg-white">
        <div
          aria-hidden="true"
          data-testid="areas-map-bg"
          className="absolute inset-0 bg-cover bg-center opacity-90 pointer-events-none"
          style={{ backgroundImage: "url('/images/areas-map.jpg')" }}
        />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.85)_0%,rgba(255,255,255,0.35)_55%,rgba(255,255,255,0)_100%)]" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-medium text-green-700 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Where We Work</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Service Areas
            </h2>
          </div>
          
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
            {['Oklahoma City', 'Edmond', 'Midwest City', 'Del City', 'Choctaw', 'Forest Park', 'Spencer'].map((city, i) => (
              <div key={city} className="reveal pop" style={{ transitionDelay: `${i * 90}ms` }}>
              <div 
                data-testid={`area-${city.toLowerCase().replace(/\s/g, '-')}`}
                className="bg-white border border-stone-200 rounded-full px-4 py-2 sm:px-6 sm:py-3 flex items-center gap-1.5 sm:gap-2 shadow-sm hover:border-green-400 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
              >
                <MapPin className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-700" />
                <span className="font-medium text-stone-800 text-xs sm:text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>{city}</span>
              </div>
              </div>
            ))}
          </div>
          
          <p className="text-center mt-6 sm:mt-10 text-stone-500 text-sm">
            Don't see your area? <a href="#contact" className="text-green-700 font-semibold hover:underline">Contact us</a> to check availability.
          </p>
        </div>
        <GrassDivider color="#9ccc65" />
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="relative overflow-hidden py-16 md:py-24 lg:py-32 bg-lime-50">
        <SectionDecor />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-medium text-green-700 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>See The Results</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Our Work
            </h2>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
            {visiblePhotos.map((image, index) => (
              <div 
                key={image} 
                data-testid={`gallery-image-${index}`}
                className="group relative overflow-hidden rounded-xl sm:rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500"
              >
                <img 
                  src={image} 
                  alt={`Lawn care project ${index + 1}`} 
                  className="w-full h-40 sm:h-60 lg:h-80 object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end">
                  <div className="p-4 sm:p-6">
                    <p className="text-white font-semibold text-sm sm:text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>Professional Results</p>
                    <p className="text-white/70 text-xs sm:text-sm">Walker Lawn Care</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-8 sm:mt-12">
            <Button 
              data-testid="gallery-toggle-btn"
              onClick={() => setShowAllPhotos(!showAllPhotos)}
              variant="outline"
              size="lg"
              className="border-2 border-green-700 text-green-700 hover:bg-lime-100 px-6 py-4 sm:px-8 sm:py-5 rounded-full font-semibold transition-all text-sm sm:text-base"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {showAllPhotos ? 'Show Less' : 'Show More'}
              <ChevronRight className={`ml-2 h-4 w-4 sm:h-5 sm:w-5 transition-transform ${showAllPhotos ? 'rotate-90' : ''}`} />
            </Button>
          </div>
        </div>
        <GrassDivider color="#9ccc65" />
      </section>

      {/* Reviews Section */}
      <section id="reviews" className="relative overflow-hidden py-16 md:py-24 lg:py-32 bg-white">
        <SectionDecor flip />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-medium text-green-700 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Reviews</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              What Our Customers Say
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {visibleReviews.map((testimonial, index) => (
              <Card 
                key={`${testimonial.name}-${index}`} 
                data-testid={`review-card-${index}`}
                className="bg-white border border-stone-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 rounded-2xl"
              >
                <CardContent className="p-5 sm:p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 sm:h-5 sm:w-5 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <p className="text-stone-600 mb-5 leading-relaxed italic text-sm sm:text-base">"{testimonial.text}"</p>
                  <div className="flex items-center gap-3 pt-3 border-t border-stone-100">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-green-700 font-bold text-xs sm:text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>{testimonial.name[0]}</span>
                    </div>
                    <div>
                      <p className="font-bold text-stone-900 text-xs sm:text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>{testimonial.name}</p>
                      <p className="text-stone-400 text-xs">{testimonial.location}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-8 sm:mt-12">
            <Button 
              data-testid="reviews-toggle-btn"
              onClick={() => setShowAllReviews(!showAllReviews)}
              variant="outline"
              size="lg"
              className="border-2 border-green-700 text-green-700 hover:bg-lime-100 px-6 py-4 sm:px-8 sm:py-5 rounded-full font-semibold transition-all text-sm sm:text-base"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {showAllReviews ? 'See Less' : 'See More Reviews'}
              <ChevronRight className={`ml-2 h-4 w-4 sm:h-5 sm:w-5 transition-transform ${showAllReviews ? 'rotate-90' : ''}`} />
            </Button>
          </div>
        </div>
        <GrassDivider color="#9ccc65" />
      </section>

      {/* Contact / Quote Section */}
      <section id="contact" className="relative overflow-hidden py-16 md:py-24 lg:py-32 bg-lime-50">
        <SectionDecor />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <div>
              <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-medium text-green-700 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Get Started</p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-stone-900 mb-4 sm:mb-6 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Get Your Free Quote
              </h2>
              <p className="text-base sm:text-lg text-stone-600 mb-8 leading-relaxed">
                Ready to transform your lawn? Fill out the form and we'll get back to you within 24 hours.
              </p>
              
              <div className="space-y-4 sm:space-y-6">
                <a href="tel:5722057331" data-testid="contact-phone-link" className="flex items-center group">
                  <div className="bg-green-100 rounded-xl sm:rounded-2xl p-3 sm:p-4 mr-4 sm:mr-5 group-hover:bg-green-200 transition-colors flex-shrink-0">
                    <Phone className="h-5 w-5 sm:h-6 sm:w-6 text-green-700" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-stone-500 mb-0.5">Call Us</p>
                    <p className="text-base sm:text-lg font-semibold text-green-700" style={{ fontFamily: "'Outfit', sans-serif" }}>(572) 205-7331</p>
                  </div>
                </a>
                
                <a href="mailto:walkerlawncare21@gmail.com" data-testid="contact-email-link" className="flex items-center group">
                  <div className="bg-green-100 rounded-xl sm:rounded-2xl p-3 sm:p-4 mr-4 sm:mr-5 group-hover:bg-green-200 transition-colors flex-shrink-0">
                    <Mail className="h-5 w-5 sm:h-6 sm:w-6 text-green-700" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-stone-500 mb-0.5">Email Us</p>
                    <p className="text-sm sm:text-lg font-semibold text-green-700 break-all" style={{ fontFamily: "'Outfit', sans-serif" }}>walkerlawncare21@gmail.com</p>
                  </div>
                </a>
                
                <div className="flex items-center">
                  <div className="bg-green-100 rounded-xl sm:rounded-2xl p-3 sm:p-4 mr-4 sm:mr-5 flex-shrink-0">
                    <MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-green-700" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-stone-500 mb-0.5">Service Areas</p>
                    <p className="text-stone-700 font-medium text-sm sm:text-base">OKC, Edmond, Midwest City, Del City, Choctaw, Forest Park, Spencer</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <Card className="border border-stone-200 shadow-2xl rounded-3xl overflow-hidden">
                <CardContent className="p-8 sm:p-10">
                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="quote-form">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-stone-800 mb-1.5" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        Your Name *
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        data-testid="quote-name-input"
                        value={formData.name}
                        onChange={handleChange}
                        className="border-stone-300 focus:border-green-500 focus:ring-green-500 rounded-xl h-12"
                        placeholder="John Doe"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-stone-800 mb-1.5" style={{ fontFamily: "'Outfit', sans-serif" }}>
                          Email *
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          data-testid="quote-email-input"
                          value={formData.email}
                          onChange={handleChange}
                          className="border-stone-300 focus:border-green-500 focus:ring-green-500 rounded-xl h-12"
                          placeholder="john@example.com"
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-semibold text-stone-800 mb-1.5" style={{ fontFamily: "'Outfit', sans-serif" }}>
                          Phone *
                        </label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          required
                          data-testid="quote-phone-input"
                          value={formData.phone}
                          onChange={handleChange}
                          className="border-stone-300 focus:border-green-500 focus:ring-green-500 rounded-xl h-12"
                          placeholder="(555) 123-4567"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="location" className="block text-sm font-semibold text-stone-800 mb-1.5" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        Address
                      </label>
                      <Input
                        id="location"
                        name="location"
                        type="text"
                        data-testid="quote-location-input"
                        value={formData.location}
                        onChange={handleChange}
                        className="border-stone-300 focus:border-green-500 focus:ring-green-500 rounded-xl h-12"
                        placeholder="123 Main St, Oklahoma City, OK"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-semibold text-stone-800 mb-1.5" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        Message
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        rows={3}
                        data-testid="quote-message-input"
                        value={formData.message}
                        onChange={handleChange}
                        className="border-stone-300 focus:border-green-500 focus:ring-green-500 rounded-xl"
                        placeholder="Tell us about your lawn care needs..."
                      />
                    </div>
                    <Button 
                      type="submit" 
                      size="lg"
                      disabled={isSubmitting}
                      data-testid="quote-submit-btn"
                      className="w-full bg-green-700 hover:bg-green-800 text-white py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      {isSubmitting ? 'Sending...' : 'Request Free Quote'}
                      {!isSubmitting && <ArrowRight className="ml-2 h-5 w-5" />}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
        <GrassDivider color="#9ccc65" />
      </section>

      {/* About / My Journey Section */}
      <section id="about" className="relative overflow-hidden py-16 md:py-24 lg:py-32 bg-white">
        <SectionDecor flip />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-medium text-green-700 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Our Story</p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-stone-900 mb-6 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                My Journey
              </h2>
              <div className="space-y-4 text-base sm:text-lg text-stone-600 leading-relaxed">
                <p>
                  When I started mowing lawns, I was just a kid loading a beat-up mower into the back of my mom's minivan, 
                  hoping someone would trust me with their yard.
                </p>
                <p>
                  Looking back now, I never imagined I'd come this far. I've gone through struggles, long days in the heat, 
                  mistakes, breakdowns, and moments where I honestly didn't know if any of this would work out.
                </p>
                <p>
                  But this community kept me going. Every person who gave me a chance, every yard I got to mow, every kind 
                  word or recommendation — it all built me into who I am and what this business has become.
                </p>
                <p>
                  I'm truly grateful for every single person who supported me, believed in me, and helped me grow. This business 
                  is more than a job — it's my story, my future, and something I've poured my whole heart into.
                </p>
                <p className="font-semibold text-green-700 text-xl italic">
                  All glory to God.
                </p>
                <p className="font-bold text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  — Daniel Walker, Owner
                </p>
              </div>
            </div>
            
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="rounded-2xl overflow-hidden shadow-lg">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_walker-lawn-care/artifacts/fmb9tbc1_IMG_0859.jpeg" 
                      alt="Walker Lawn Care work" 
                      className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="rounded-2xl overflow-hidden shadow-lg">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_walker-lawn-care/artifacts/d2rqp3b8_IMG_0854.jpeg" 
                      alt="Walker Lawn Care work" 
                      className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  <div className="rounded-2xl overflow-hidden shadow-lg">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_walker-lawn-care/artifacts/5w6c6ehq_IMG_0860.jpeg" 
                      alt="Walker Lawn Care work" 
                      className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="rounded-2xl overflow-hidden shadow-lg">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_walker-lawn-care/artifacts/ssthkmpf_IMG_0861.jpeg" 
                      alt="Walker Lawn Care work" 
                      className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </div>
              </div>
              
              <div className="absolute -bottom-6 -left-6 bg-green-700 text-white rounded-2xl shadow-2xl p-6 max-w-xs">
                <p className="font-bold text-xl mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Community Driven</p>
                <p className="text-green-100 text-sm">Built on trust, hard work, and passion</p>
              </div>
            </div>
          </div>
        </div>
        <GrassDivider color="#15803d" />
      </section>

      {/* CTA Band */}
      <section className="py-12 md:py-16 bg-green-700">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Ready for a lawn you'll love?
          </h2>
          <p className="text-green-100 text-sm sm:text-lg mb-6 sm:mb-8">Get your free estimate today — no obligation, no hassle.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
            <Button 
              size="lg"
              data-testid="cta-quote-btn"
              className="bg-white text-green-800 hover:bg-stone-50 px-7 py-5 sm:px-8 sm:py-6 text-base sm:text-lg rounded-full font-semibold shadow-xl transition-all"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            >
              Get Free Quote
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg"
              data-testid="cta-call-btn"
              className="bg-green-800 text-white hover:bg-green-900 border-2 border-green-500 px-7 py-5 sm:px-8 sm:py-6 text-base sm:text-lg rounded-full font-semibold transition-all"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              onClick={() => window.location.href = 'tel:5722057331'}
            >
              <Phone className="mr-2 h-5 w-5" />
              Call (572) 205-7331
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-900 text-white pt-12 md:pt-16 pb-6 md:pb-8">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-12 mb-10 sm:mb-12">
            <div>
              <img 
                src="/images/logo-transparent.png" 
                alt="Walker Lawn Care Logo" 
                className="h-16 w-auto mb-5 object-contain [filter:drop-shadow(0_0_1px_rgba(255,255,255,0.9))_drop-shadow(0_0_1px_rgba(255,255,255,0.9))]" data-testid="footer-logo"
              />
              <p className="text-stone-400 leading-relaxed">
                Professional lawn care services serving the Oklahoma City metro area since 2023.
              </p>
            </div>
            
            <div>
              <h3 className="font-bold text-lg mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>Quick Links</h3>
              <ul className="space-y-3">
                {['About', 'Services', 'Service Areas', 'Gallery', 'Reviews', 'Contact'].map(link => (
                  <li key={link}>
                    <a href={`#${link.toLowerCase().replace(/\s/g, '')}`} className="text-stone-400 hover:text-white transition-colors">
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h3 className="font-bold text-lg mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>Contact Info</h3>
              <ul className="space-y-3 text-stone-400">
                <li>
                  <a href="tel:5722057331" className="flex items-center gap-2 hover:text-white transition-colors">
                    <Phone className="h-5 w-5 text-green-400" />
                    (572) 205-7331
                  </a>
                </li>
                <li>
                  <a href="mailto:walkerlawncare21@gmail.com" className="flex items-center gap-2 hover:text-white transition-colors break-all">
                    <Mail className="h-5 w-5 text-green-400" />
                    walkerlawncare21@gmail.com
                  </a>
                </li>
              </ul>
              
              <h3 className="font-bold text-lg mb-4 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>Socials</h3>
              <ul className="space-y-3 text-stone-400">
                <li>
                  <a href="https://www.instagram.com/walkerlawncare2023/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors">
                    <Instagram className="h-5 w-5 text-green-400" />
                    @walkerlawncare2023
                  </a>
                </li>
                <li>
                  <a href="https://nextdoor.com/pages/walker-lawn-care-oklahoma-city-ok/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors">
                    <HomeIcon className="h-5 w-5 text-green-400" />
                    @walkerlawncare
                  </a>
                </li>
              </ul>
              
              {/* Employee Sign In */}
              <div className="mt-6 pt-6 border-t border-stone-800">
                <h3 className="font-semibold text-sm mb-3 text-stone-500" style={{ fontFamily: "'Outfit', sans-serif" }}>Employee Sign In</h3>
                <form className="space-y-2" onSubmit={handleLogin}>
                  <Input
                    type="text"
                    name="username"
                    placeholder="Username"
                    value={loginData.username}
                    onChange={handleLoginChange}
                    required
                    data-testid="login-username-input"
                    className="bg-stone-800 border-stone-700 text-white placeholder:text-stone-500 h-9 text-sm rounded-lg"
                  />
                  <Input
                    type="password"
                    name="password"
                    placeholder="Password"
                    value={loginData.password}
                    onChange={handleLoginChange}
                    required
                    data-testid="login-password-input"
                    className="bg-stone-800 border-stone-700 text-white placeholder:text-stone-500 h-9 text-sm rounded-lg"
                  />
                  <Button 
                    type="submit"
                    size="sm"
                    disabled={isLoggingIn}
                    data-testid="login-submit-btn"
                    className="w-full bg-green-700 hover:bg-green-800 text-white text-sm disabled:opacity-50 rounded-lg"
                  >
                    {isLoggingIn ? 'Signing in...' : 'Sign In'}
                  </Button>
                </form>
              </div>
            </div>
          </div>
          
          <div className="border-t border-stone-800 pt-8 text-center text-stone-500 text-sm">
            <p>&copy; {new Date().getFullYear()} Walker Lawn Care. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
